/**
 * Client helpers for getting an image into the form — from an upload or from
 * the search sidebar.
 *
 * Photo results are pulled through `/api/images/file`, because the provider CDNs
 * Openverse federates over don't reliably send CORS headers. Logos are fetched
 * straight from the page, because Simple Icons does — see `downloadCandidates`
 * for why that distinction is load-bearing. SVG results are rasterised to PNG
 * here so the renderer only ever deals with sources that have real pixel
 * dimensions.
 */
import type { PickedImage } from "./types";

/** Logos are square marks; 512 keeps them crisp inside a ~470px card. */
const SVG_RASTER_SIZE = 512;
/** Used only when the browser reports no intrinsic size for a source. */
const FALLBACK_SIZE = 1024;
/** Matches the proxy's own upstream deadline, plus room for the round trip. */
const DOWNLOAD_TIMEOUT_MS = 20_000;

export interface ImageSearchResult {
	id: string;
	title: string;
	thumbUrl: string;
	downloadUrl: string;
	source: "logos" | "photos";
	license?: string;
	attribution?: string;
}

export interface ImageSearchResponse {
	source: "logos" | "photos";
	page: number;
	/** True when nothing was typed and these are the suggestions. */
	featured: boolean;
	results: ImageSearchResult[];
	hasMore: boolean;
}

export function proxiedUrl({ url }: { url: string }): string {
	return `/api/images/file?url=${encodeURIComponent(url)}`;
}

export async function searchImages({
	query,
	source,
	page = 1,
	signal,
}: {
	query: string;
	source: "logos" | "photos";
	page?: number;
	signal?: AbortSignal;
}): Promise<ImageSearchResponse> {
	const params = new URLSearchParams({
		q: query,
		source,
		page: String(page),
	});

	const response = await fetch(`/api/images/search?${params}`, { signal });
	if (!response.ok) {
		throw new Error(
			response.status === 429
				? "Too many searches. Wait a moment."
				: "Image search is unavailable right now.",
		);
	}

	return (await response.json()) as ImageSearchResponse;
}

export async function pickedImageFromFile({
	file,
}: {
	file: File;
}): Promise<PickedImage> {
	if (file.type === "image/svg+xml") {
		const png = await rasterize({ blob: file, name: replaceExtension({ name: file.name }) });
		return pickedImageFromFile({ file: png });
	}

	const previewUrl = URL.createObjectURL(file);
	try {
		const { width, height } = await measure({ url: previewUrl });
		return { previewUrl, file, width, height };
	} catch (error) {
		// The caller retries with another source, so a failed measure must not
		// leave its object URL behind.
		URL.revokeObjectURL(previewUrl);
		throw error;
	}
}

export async function pickedImageFromSearchResult({
	result,
}: {
	result: ImageSearchResult;
}): Promise<PickedImage> {
	let slow = false;
	for (const url of downloadCandidates({ result })) {
		const blob = await download({ url }).catch((error: unknown) => {
			slow ||= isTimeout({ error });
			return null;
		});
		if (!blob) continue;

		// Decoding is part of the attempt: a provider that answers with a
		// truncated or mislabelled file should fall through to the next source
		// rather than land in the form as a broken slot.
		const picked = await pickedImageFromBlob({
			blob,
			title: result.title,
		}).catch(() => null);
		if (!picked) continue;

		return result.attribution
			? { ...picked, attribution: result.attribution }
			: picked;
	}

	throw new Error(
		slow
			? "That image took too long to download. Try another."
			: "That image couldn't be downloaded. Try another, or upload one.",
	);
}

/**
 * Every place a result's bytes might come from, best first.
 *
 * `downloadUrl` is the provider's own original: the best copy, and the one with
 * more ways to fail. A provider can block hotlinking, a record can go stale
 * behind Openverse's index, and a full-resolution original can exceed the
 * proxy's 6MB cap — all of which used to surface as one flat "couldn't
 * download" with nothing else to try. (Sampling 50 results across five queries
 * turned up none of these, so this is the uncommon case rather than the normal
 * one.) The thumbnail is a ~600px rendition from Openverse's CDN, below what the
 * card deserves but far better than sending someone off to find another picture.
 *
 * Logos are tried unproxied first, and that ordering is the fix for a real bug
 * rather than an optimisation. The proxy fetches from the server's address, and
 * the server's address is not the user's: cdn.simpleicons.org serves the browser
 * happily but refuses us, so in production every logo failed with "that image
 * couldn't be downloaded" while the identical URL sat rendering in the grid
 * beside it — the tiles preview with a plain `<img>`, which never goes through
 * the proxy. Simple Icons answers `access-control-allow-origin: *`, so the page
 * can just read the bytes, which puts the download back on the one path the
 * preview has already proved works. The proxy stays behind it for a network that
 * blocks the CDN from the browser too.
 */
function downloadCandidates({ result }: { result: ImageSearchResult }): string[] {
	const urls = [result.downloadUrl, result.thumbUrl].filter(
		(url, index, all) => Boolean(url) && all.indexOf(url) === index,
	);
	const proxied = urls.map((url) => proxiedUrl({ url }));

	return result.source === "logos" ? [...urls, ...proxied] : proxied;
}

/**
 * Openverse federates over provider CDNs that sometimes hang; without a deadline
 * the picker would sit on a spinner with nothing to report.
 */
async function download({ url }: { url: string }): Promise<Blob> {
	const response = await fetch(url, {
		signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS),
	});
	if (!response.ok) throw new Error(`image request failed: ${response.status}`);
	return await response.blob();
}

async function pickedImageFromBlob({
	blob,
	title,
}: {
	blob: Blob;
	title: string;
}): Promise<PickedImage> {
	const safeName = title.replace(/[^\w.-]+/g, "-").slice(0, 48) || "image";

	const file =
		blob.type === "image/svg+xml"
			? await rasterize({ blob, name: `${safeName}.png` })
			: new File([blob], `${safeName}${extensionFor({ type: blob.type })}`, {
					type: blob.type,
				});

	return await pickedImageFromFile({ file });
}

/**
 * `AbortSignal.timeout` rejects with a `DOMException` named `TimeoutError`.
 * Matched by name rather than by type: there is no distinct constructor to test
 * against, and a plain network failure is a `TypeError` we want to tell apart.
 */
function isTimeout({ error }: { error: unknown }): boolean {
	return (error as { name?: string } | null)?.name === "TimeoutError";
}

export function releasePickedImage({ image }: { image: PickedImage | null }): void {
	if (!image) return;
	try {
		URL.revokeObjectURL(image.previewUrl);
	} catch {
		/* already revoked */
	}
}

async function rasterize({
	blob,
	name,
}: {
	blob: Blob;
	name: string;
}): Promise<File> {
	const url = URL.createObjectURL(blob);
	try {
		const image = await load({ url });
		// SVGs frequently report no intrinsic size, so square is the safe default
		// (every Simple Icons mark is square anyway).
		const naturalWidth = image.naturalWidth || SVG_RASTER_SIZE;
		const naturalHeight = image.naturalHeight || SVG_RASTER_SIZE;
		const scale = SVG_RASTER_SIZE / Math.max(naturalWidth, naturalHeight);
		const width = Math.round(naturalWidth * scale);
		const height = Math.round(naturalHeight * scale);

		const canvas = document.createElement("canvas");
		canvas.width = width;
		canvas.height = height;
		const ctx = canvas.getContext("2d");
		if (!ctx) throw new Error("Canvas 2D is unavailable");
		ctx.drawImage(image, 0, 0, width, height);

		const png = await new Promise<Blob | null>((resolve) =>
			canvas.toBlob(resolve, "image/png"),
		);
		if (!png) throw new Error("Couldn't convert that image.");

		return new File([png], name, { type: "image/png" });
	} finally {
		URL.revokeObjectURL(url);
	}
}

async function measure({ url }: { url: string }): Promise<{
	width: number;
	height: number;
}> {
	const image = await load({ url });
	return {
		width: image.naturalWidth || FALLBACK_SIZE,
		height: image.naturalHeight || FALLBACK_SIZE,
	};
}

function load({ url }: { url: string }): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const image = new window.Image();
		image.onload = () => resolve(image);
		image.onerror = () => reject(new Error("That file isn't a readable image."));
		image.src = url;
	});
}

function extensionFor({ type }: { type: string }): string {
	if (type.includes("png")) return ".png";
	if (type.includes("webp")) return ".webp";
	if (type.includes("gif")) return ".gif";
	return ".jpg";
}

function replaceExtension({ name }: { name: string }): string {
	return `${name.replace(/\.[^.]+$/, "")}.png`;
}
